"use client";

import { useClientTranslations } from "@/lib/translate";
import styles from "./Signup.module.css";
import { Input } from "@/modules/components/commons";
import Tooltip from "@/modules/components/commons/Tooltip";
import { EyeIcon, ViewOffIcon } from "hugeicons-react";
import { useSignup } from "@/modules/hooks/signup/useSignup";
import Checkbox from "@/modules/components/commons/Checkbox";
import Link from "next/link";
import Image from "next/image";
import AppleLogo from "@/assets/imgs/apple-logo.svg";
import GoogleLogo from "@/assets/imgs/google-logo.svg";
import { routes } from "@/lib/utils/routes";
import CircularProgress from "@/modules/components/commons/CircularProgress";

const SignupAuthInfo = () => {
  const { t } = useClientTranslations("signup-page");
  const {
    loading,
    email,
    signup,
    password,
    repeatPassword,
    setEmail,
    setPassword,
    setRepeatPassword,
    showPassword,
    showRepeatPassword,
    readTerms,
    setShowPassword,
    setShowRepeatPassword,
    setReadTerms,
    errorEmail,
    errorPassword,
    errorRepeatPassword,
    errorSubmit,
    errorTerms,
    handleGoogleLogin,
  } = useSignup();

  return (
    <div>
      <div className="w-full grid grid-cols-2 gap-3">
        <Tooltip content={t("step1")}>
          <div className="w-full h-1 bg-primary rounded my-3"></div>
        </Tooltip>
        <Tooltip content={t("step2")}>
          <div className="w-full h-1 bg-gray-300 dark:bg-gray-700 rounded my-3"></div>
        </Tooltip>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          signup();
        }}
        className={`${styles.form} mt-5`}
      >
        <h1 className="text-2xl font-bold mb-7 text-primary dark:text-white">
          {t("hello")}
        </h1>
        <div>
          <Input
            id="email"
            type="email"
            placeholder={t("signup.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errorEmail}
            required
          />
        </div>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder={t("signup.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errorPassword}
            required
          />
          <button
            type="button"
            className="absolute right-2 top-0 text-sm text-primary translate-y-1"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeIcon /> : <ViewOffIcon />}
          </button>
        </div>
        <div className="relative">
          <Input
            id="repeatPassword"
            type={showRepeatPassword ? "text" : "password"}
            placeholder={t("signup.repeatPassword")}
            value={repeatPassword}
            onChange={(e) => setRepeatPassword(e.target.value)}
            error={errorRepeatPassword}
            required
          />
          <button
            type="button"
            className="absolute right-2 top-0 text-sm text-primary translate-y-1"
            onClick={() => setShowRepeatPassword(!showRepeatPassword)}
          >
            {showRepeatPassword ? <EyeIcon /> : <ViewOffIcon />}
          </button>
        </div>
        {errorSubmit && <p className="text-red-500 text-sm">{errorSubmit}</p>}
        <div className="flex flex-row gap-3 items-center">
          <Checkbox
            id="remember-me"
            checked={readTerms}
            onChange={() => setReadTerms(!readTerms)}
          />
          <p>
            {t("signup.read")}{" "}
            <Link
              href={routes.about.term}
              className="text-primary font-semibold hover:underline"
            >
              {t("signup.terms")}
            </Link>{" "}
            {t("signup.and")}{" "}
            <Link
              href={routes.about.privacy}
              className="text-primary font-semibold hover:underline"
            >
              {t("signup.privacy")}
            </Link>
          </p>
        </div>
        {errorTerms && <p className="text-red-500 text-sm">{errorTerms}</p>}
        <button
          type="submit"
          disabled={!readTerms}
          className="flex justify-center items-center bg-gradient-to-r from-purple-400 hover:from-purple-500 to-purple-600 hover:to-purple-700 focus:ring-purple-500 focus:outline-none focus:ring-4 text-white py-2 rounded-lg disabled:cursor-not-allowed"
        >
          {loading ? <CircularProgress /> : t("signup.button")}
        </button>
      </form>
      <div className="mt-7 grid grid-cols-1 gap-2 divide-y divide-gray-700 dark:divide-gray-100">
        <div className="flex flex-row justify-between">
          <div className="flex flex-row gap-1">
            <p className="text-sm">{t("signup.loginQuestion")}</p>
            <Link className="text-primary text-sm" href={routes.login}>
              {t("signup.login")}
            </Link>
          </div>
          <Link className="text-primary text-sm" href={routes.forgotPassword}>
            {t("signup.forgotPassword")}
          </Link>
        </div>
        <div className="flex flex-row gap-1 pt-3">
          <button 
            type="button" 
            onClick={() => handleGoogleLogin()} 
            className="w-full rounded-lg bg-white p-1 flex justify-center items-center border-2 border-gray-300 dark:border-none"
          >
            <Image
              src={GoogleLogo}
              width={40}
              height={40}
              alt="Together Work Login Google"
            />
          </button>
          <button className="w-full rounded-lg bg-white p-1 flex justify-center items-center border-2 border-gray-300 dark:border-none">
            <Image
              src={AppleLogo}
              width={30}
              height={30}
              alt="Together Work Login Apple"
            />
          </button>
        </div>
      </div>
    </div>
  );
};

SignupAuthInfo.displayName = "SignupAuthInfo";
export default SignupAuthInfo;
